import { Link } from "react-router-dom";
import { useSEO, breadcrumb } from "../lib/seo";
import { SITE } from "../lib/site";
import PageHero from "../components/PageHero";
import { UserPlus, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const STEPS = [
  { num: "01", title: "Buka halaman pendaftaran resmi", desc: "Akses melalui menu utama atau halaman link alternatif resmi Sloto69." },
  { num: "02", title: "Klik tombol Daftar", desc: "Tombol pendaftaran biasanya tersedia di hero section atau navbar utama." },
  { num: "03", title: "Lengkapi formulir dengan teliti", desc: "Isi data sesuai instruksi. Hindari kesalahan ketik agar verifikasi berjalan lancar." },
  { num: "04", title: "Periksa kembali sebelum mengirim", desc: "Pastikan semua informasi sudah benar. Pendaftaran data akurat memudahkan proses akun ke depan." },
  { num: "05", title: "Selesaikan proses verifikasi", desc: "Ikuti instruksi yang muncul untuk memvalidasi pendaftaran Anda." },
  { num: "06", title: "Simpan informasi akun dengan aman", desc: "Catat informasi penting di tempat aman dan hindari berbagi dengan pihak lain." },
];

export default function RegisterGuide() {
  useSEO({
    title: "Panduan Daftar Sloto69 | Cara Mendaftar untuk Pengguna Baru",
    description: "Panduan daftar Sloto69 lengkap dan ramah pengguna baru: langkah-langkah, persiapan, dan tips menjaga akun setelah pendaftaran.",
    keywords: "sloto69 daftar, panduan daftar sloto69, cara daftar sloto69",
    canonical: `${SITE.baseUrl}/panduan-daftar`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Panduan Daftar", url: `${SITE.baseUrl}/panduan-daftar` },
    ]),
  });

  return (
    <>
      <PageHero
        eyebrow="Panduan Daftar"
        title="Panduan Daftar Sloto69 untuk Pengguna Baru"
        description="Pelajari proses pendaftaran Sloto69 secara bertahap, mulai dari persiapan data hingga praktik terbaik setelah akun Anda aktif."
        breadcrumb={[{ label: "Panduan Daftar" }]}
      />

      <section className="section-pad" data-testid="register-prep">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="gold-pill">Persiapan</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Persiapkan Hal-Hal Berikut Sebelum Mendaftar
            </h2>
            <p className="mt-4 text-slate-300">
              Persiapan yang baik membantu proses pendaftaran berjalan lancar tanpa hambatan teknis.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
              {[
                "Jaringan internet yang stabil.",
                "Email aktif yang dapat diverifikasi.",
                "Nomor kontak yang valid.",
                "Peramban yang mendukung cookie.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <a href={SITE.registerUrl} data-testid="registerguide-cta" className="btn-primary mt-8 inline-flex">
              <UserPlus className="w-4 h-4" /> Daftar Sekarang
            </a>
          </div>

          <ol className="lg:col-span-7 space-y-4">
            {STEPS.map((s, i) => (
              <li key={s.num} data-testid={`register-step-${i}`} className="card-premium p-6 flex gap-5">
                <div className="step-number w-12 h-12 rounded-xl flex items-center justify-center text-base flex-shrink-0">
                  {s.num}
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-pad bg-[#070710] border-y border-white/5" data-testid="register-after">
        <div className="shell grid lg:grid-cols-2 gap-8">
          <div className="card-premium p-7">
            <h3 className="font-display text-2xl font-semibold">Setelah Berhasil Mendaftar</h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              Setelah akun Anda aktif, ada beberapa kebiasaan baik yang sebaiknya diterapkan
              agar pengalaman penggunaan tetap nyaman dan aman.
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li>• Bookmark halaman resmi untuk akses cepat.</li>
              <li>• Pelajari halaman Keamanan Akun.</li>
              <li>• Kunjungi halaman Artikel untuk informasi tambahan.</li>
              <li>• Catat kanal kontak resmi untuk antisipasi kebutuhan.</li>
            </ul>
          </div>
          <div className="card-premium p-7">
            <AlertTriangle className="w-8 h-8 text-[#D4AF37]" />
            <h3 className="mt-4 font-display text-2xl font-semibold">Kewaspadaan Penting</h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              Jangan pernah membagikan informasi akun, hindari pendaftaran melalui sumber tidak
              dikenal, dan selalu kunjungi halaman resmi sebagai rujukan utama.
            </p>
            <Link to="/keamanan" data-testid="register-keamanan-link" className="btn-secondary mt-6 inline-flex">
              Lihat Halaman Keamanan <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
